import { useRef, useState } from 'react';
import { motion, useScroll, useTransform, useSpring, useMotionValue } from 'framer-motion';
import { ArrowRight, BadgeCheck } from 'lucide-react';
import { useLang } from '@/context/LanguageContext';
import { scrollToId } from './LenisScroll';
import { TENURES, buildQuote, inr, dueDate, AMOUNT_MIN, AMOUNT_MAX, AMOUNT_STEP } from '@/lib/loan';

const HERO_IMG =
  'https://images.pexels.com/photos/30535634/pexels-photo-30535634.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

export default function Hero() {
  const { t, lang, locale } = useLang();
  const [amount, setAmount] = useState(25000);
  const [tenure, setTenure] = useState(30);
  const sectionRef = useRef(null);

  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start start', 'end start'] });
  const yGlow = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yCard = useTransform(scrollYProgress, [0, 1], [0, -50]);

  const rx = useSpring(useMotionValue(0), { stiffness: 120, damping: 14 });
  const ry = useSpring(useMotionValue(0), { stiffness: 120, damping: 14 });

  const onTilt = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    ry.set(px * 8);
    rx.set(-py * 8);
  };
  const resetTilt = () => {
    rx.set(0);
    ry.set(0);
  };

  const quote = buildQuote(amount, tenure);
  const pct = ((amount - AMOUNT_MIN) / (AMOUNT_MAX - AMOUNT_MIN)) * 100;

  const sendPrefill = () => {
    window.dispatchEvent(new CustomEvent('pdi-prefill', { detail: { amount, tenure_days: tenure } }));
    scrollToId('apply');
  };

  return (
    <section ref={sectionRef} className="relative overflow-hidden pb-24 pt-32 sm:pt-36 lg:pb-28" data-testid="hero-section">
      <motion.div style={{ y: yGlow }} className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 right-[-10%] h-[560px] w-[560px] rounded-full bg-pulse/10 blur-[140px]" />
        <div className="absolute bottom-[-20%] left-[-10%] h-[420px] w-[420px] rounded-full bg-gold/5 blur-[120px]" />
        <div
          className="absolute inset-0 opacity-[0.35]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px)',
            backgroundSize: '56px 56px',
            maskImage: 'radial-gradient(ellipse 90% 70% at 50% 0%, black 30%, transparent 75%)',
            WebkitMaskImage: 'radial-gradient(ellipse 90% 70% at 50% 0%, black 30%, transparent 75%)',
          }}
        />
      </motion.div>

      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-[1.05fr_0.95fr] lg:gap-10">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-7 inline-flex items-center gap-2 rounded-full border border-pulse/25 bg-pulse/10 px-4 py-1.5"
            data-testid="hero-badge"
          >
            <BadgeCheck size={14} className="text-pulse" />
            <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-pulse">
              {t.hero.badge}
            </span>
          </motion.div>

          <h1
            key={lang}
            className="font-display text-[13.5vw] font-extrabold leading-[0.98] tracking-tight text-white sm:text-7xl lg:text-[5.4rem]"
            data-testid="hero-title"
          >
            {t.hero.lines.map((line, i) => (
              <span key={i} className="mask-line">
                <motion.span
                  className={`block ${i === 1 ? 'italic text-pulse' : ''}`}
                  initial={{ y: '112%' }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.12 + i * 0.13, ease: [0.16, 1, 0.3, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            className="mt-7 max-w-lg text-base leading-relaxed text-slate-400 sm:text-lg"
            data-testid="hero-subtitle"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-apply-button"
              onClick={sendPrefill}
              className="group flex items-center gap-2 rounded-full bg-pulse px-7 py-3.5 font-display text-base font-bold text-ink transition-transform hover:scale-[1.03] hover:bg-pulse-dim glow-pulse"
            >
              {t.hero.ctaApply}
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-how-button"
              onClick={() => scrollToId('how')}
              className="rounded-full border border-white/15 px-7 py-3.5 text-base font-semibold text-slate-200 transition-colors hover:border-pulse/50 hover:text-pulse"
            >
              {t.hero.ctaHow}
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-12 flex flex-wrap gap-x-10 gap-y-4 border-t border-white/10 pt-7"
            data-testid="hero-stats"
          >
            {t.hero.stats.map((s) => (
              <div key={s.l}>
                <p className="font-mono text-xl font-bold text-white sm:text-2xl">{s.v}</p>
                <p className="mt-1 text-xs uppercase tracking-wider text-slate-500">{s.l}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div style={{ y: yCard }} className="relative mx-auto w-full max-w-[520px] pb-10 lg:pb-0">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
            onMouseMove={onTilt}
            onMouseLeave={resetTilt}
            style={{ rotateX: rx, rotateY: ry, transformPerspective: 1000 }}
            className="relative"
          >
            <div className="overflow-hidden rounded-[2rem] border border-white/10 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.7)]">
              <img
                src={HERO_IMG}
                alt="Young Indian professional applying for an instant loan on smartphone"
                className="h-[420px] w-full object-cover contrast-[1.05] saturate-[0.85] sm:h-[500px]"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/20 to-transparent" />
              <div className="absolute inset-0 bg-pulse/5 mix-blend-overlay" />
            </div>

            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute right-4 top-5 flex items-center gap-2 rounded-full border border-white/10 bg-ink/80 px-4 py-2 backdrop-blur-xl"
              data-testid="hero-transfer-chip"
            >
              <span className="h-2 w-2 rounded-full bg-pulse" />
              <span className="font-mono text-[11px] font-semibold text-slate-200">{t.hero.transferChip}</span>
            </motion.div>
          </motion.div>

          <div
            className="absolute -bottom-2 left-1/2 w-[92%] -translate-x-1/2 rounded-2xl border border-white/10 bg-abyss/90 p-5 shadow-2xl backdrop-blur-xl sm:-left-6 sm:w-[340px] sm:translate-x-0"
            data-testid="hero-estimate-card"
          >
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-pulse">
              {t.hero.estimate}
            </p>
            <p className="mt-1 text-xs text-slate-500">{t.hero.estimateHint}</p>
            <p className="mt-3 font-mono text-3xl font-bold text-white" data-testid="hero-estimate-amount">
              {inr(amount)}
            </p>
            <input
              type="range"
              data-testid="hero-estimate-slider"
              min={AMOUNT_MIN}
              max={AMOUNT_MAX}
              step={AMOUNT_STEP}
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
              className="mt-4 w-full"
              style={{ '--fill': `${pct}%` }}
              aria-label="Loan amount estimate"
            />
            <div className="mt-4 flex items-center justify-between">
              <span className="text-xs text-slate-500">{t.hero.tenureLabel}</span>
              <div className="flex gap-1.5">
                {TENURES.filter((d) => d !== 60).map((d) => (
                  <button
                    key={d}
                    data-testid={`hero-tenure-chip-${d}`}
                    onClick={() => setTenure(d)}
                    className={`rounded-full px-2.5 py-1 font-mono text-[11px] font-semibold transition-colors ${
                      tenure === d ? 'bg-pulse text-ink' : 'bg-white/5 text-slate-400 hover:text-white'
                    }`}
                  >
                    {d}d
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-3">
              <div>
                <p className="text-[11px] uppercase tracking-wider text-slate-500">{t.hero.youRepay}</p>
                <p className="font-mono text-sm font-bold text-pulse" data-testid="hero-estimate-repayment">
                  {inr(quote.total_repayment)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[11px] uppercase tracking-wider text-slate-500">{t.hero.dueOn}</p>
                <p className="font-mono text-sm font-bold text-white" data-testid="hero-estimate-due">
                  {dueDate(tenure, locale)}
                </p>
              </div>
            </div>
            <button
              data-testid="hero-estimate-cta"
              onClick={sendPrefill}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-white/5 py-2.5 text-sm font-bold text-pulse ring-1 ring-pulse/30 transition-colors hover:bg-pulse hover:text-ink"
            >
              {t.hero.ctaThis} <ArrowRight size={15} />
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
